import { memo, useMemo } from 'react';

import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import dayjs from 'dayjs';
import { camelCase, startCase } from 'lodash';
import PropTypes from 'prop-types';

import Money from '#client/components/ListTransaction/components/Money';
import PaymentStatusLabel from '#client/components/PaymentStatusLabel';
import DirectionTypeIcon from '#client/components/TransactionDirectionTypeIcon';
import { DATE_FORMAT } from '#client/constants/date';
import { MONEY_COLOR, PAYMENT_STATUS, TRANSACTION_DIRECTION } from '#client/constants/transaction';
import useBreakpoints from '#client/hooks/useBreakpoints';
import useModalContext from '#client/hooks/useModalContext';

import PaymentDetailModal from './PaymentDetailModal';
import { StackStyled } from './PaymentRow.styles';

function PaymentRow({
  id,
  createdAt,
  summary,
  direction,
  amount,
  type,
  status,
  description,
  refetchPayment,
  estimatedCompletionDate,
  transactionId,
  reason,
  sameDay,
  from,
  to,
  counterparty,
  counterpartyIdFromTransaction
}) {
  const { openModal } = useModalContext();

  const trackButtonPressed = useButtonPressedCallback({
    elementName: 'link_account_modal',
    elementText: 'transactions_expanded',
    appPageName: 'active_banking_dashboard'
  });
  const clickHandler = () => {
    // Tracking Details: Tracking added to click on transactions to see details
    trackButtonPressed();
    openModal({
      component: PaymentDetailModal,
      summary,
      amount,
      createdAt,
      estimatedCompletionDate,
      type,
      id,
      status,
      reason: reason ? startCase(camelCase(reason)) : null,
      sameDay,
      description,
      direction,
      from,
      to,
      transactionId,
      refetchPayment,
      counterparty,
      counterpartyIdFromTransaction
    });
  };

  const {
    sizes: { isDesktop }
  } = useBreakpoints();

  const moneyColor = useMemo(() => {
    if (status !== PAYMENT_STATUS.SENT) {
      return MONEY_COLOR.TERTIARY;
    }
    if (status === PAYMENT_STATUS.SENT && direction === TRANSACTION_DIRECTION.DEBIT) {
      return MONEY_COLOR.GREEN;
    }

    return MONEY_COLOR.PRIMARY;
  }, [direction, status]);

  return (
    <Stack
      onClick={clickHandler}
      role="listitem"
      sx={{
        cursor: 'pointer',
        padding: '15px 10px',
        '&:hover': {
          boxShadow: '0px 4px 16px 2px rgba(0, 0, 0, 0.12)'
        }
      }}
    >
      <StackStyled
        gap="24px"
        pr={0}
        width="100%"
      >
        <StackStyled width="85%">
          <Stack
            alignItems="center"
            direction="row"
            spacing={{ xs: '16px', md: '32px' }}
          >
            <Stack>
              <DirectionTypeIcon
                direction={direction}
                isPayment
              />
            </Stack>
            <Stack
              alignItems="start"
              direction="column"
              spacing={1}
            >
              <Text
                size="md"
                weight="medium"
              >
                {summary}
              </Text>
              <Text
                color="tertiary"
                size="xs"
                whiteSpace="nowrap"
              >
                {dayjs(createdAt).format(DATE_FORMAT.PRIMARY)}
              </Text>
            </Stack>
          </Stack>
          {isDesktop && <PaymentStatusLabel status={status} />}
        </StackStyled>
        <Stack
          alignItems="end"
          direction="column"
        >
          <Money
            amount={amount}
            color={moneyColor}
            direction={direction}
            isPayment
          />
          {!isDesktop && <PaymentStatusLabel status={status} />}
        </Stack>
      </StackStyled>
    </Stack>
  );
}

PaymentRow.propTypes = {
  id: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  direction: PropTypes.string.isRequired,
  createdAt: PropTypes.string.isRequired,
  summary: PropTypes.string.isRequired,
  amount: PropTypes.string.isRequired,
  status: PropTypes.string.isRequired,
  description: PropTypes.string,
  estimatedCompletionDate: PropTypes.string,
  refetchPayment: PropTypes.func,
  transactionId: PropTypes.string,
  reason: PropTypes.string,
  sameDay: PropTypes.bool,
  from: PropTypes.shape({
    accountHolder: PropTypes.string,
    routingNumber: PropTypes.string,
    accountNumberLast4: PropTypes.string,
    accountNumber: PropTypes.string
  }),
  to: PropTypes.shape({
    accountHolder: PropTypes.string,
    routingNumber: PropTypes.string,
    accountNumberLast4: PropTypes.string,
    accountNumber: PropTypes.string
  }),
  counterparty: PropTypes.shape({
    counterpartyId: PropTypes.string,
    bank: PropTypes.string,
    connectedType: PropTypes.string,
    accountNumber: PropTypes.string,
    fullAccountNumber: PropTypes.string
  }),
  counterpartyIdFromTransaction: PropTypes.string
};

export default memo(PaymentRow);
